import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import { User } from '../types'
import { ProfileService } from '../utils/profileService'

interface UserProfileContextType {
  user: User | null
  isLoadingUser: boolean
  error: string | null
  refetchUser: () => Promise<void>
  getUserDisplayName: () => string
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(
  undefined
)

interface UserProfileProviderProps {
  children: ReactNode
}

export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUserProfile = useCallback(async () => {
    // Don't fetch if user is not authenticated
    const token = localStorage.getItem('accessToken')
    if (!token) {
      setIsLoadingUser(false)
      setUser(null)
      return
    }

    try {
      setIsLoadingUser(true)
      setError(null)
      const response = await ProfileService.getProfile()

      if (response.statusCode === 200 && response.response?.user) {
        setUser(response.response.user)
      } else {
        setError('Failed to fetch user profile')
      }
    } catch (err: any) {
      console.error('Error fetching user profile:', err)
      // Don't set error if it's a 401 (token expired) - logout is already handled by API client
      if (err?.status !== 401) {
        setError('Failed to fetch user profile')
      }
      // Clear user if token expired
      if (err?.status === 401) {
        setUser(null)
      }
    } finally {
      setIsLoadingUser(false)
    }
  }, [])

  const getUserDisplayName = () => {
    if (!user) return 'User'
    return `${user.firstName} ${user.lastName}`.trim() || user.email
  }

  useEffect(() => {
    // Fetch user profile on mount if token exists
    if (localStorage.getItem('accessToken')) {
      fetchUserProfile()
    }
  }, [fetchUserProfile])

  const value: UserProfileContextType = {
    user,
    isLoadingUser,
    error,
    refetchUser: fetchUserProfile,
    getUserDisplayName,
  }

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  )
}

export const useUserProfile = (): UserProfileContextType => {
  const context = useContext(UserProfileContext)
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider')
  }
  return context
}
